import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";

/**
 * AdminHealthBadge — tiny status pill that polls the API root every 60s
 * and tells the admin at a glance whether the backend is reachable.
 *
 * States:
 *   • checking  → grey dot, "Checking…"
 *   • ok        → green pulse dot, "API online"
 *   • down      → red dot, "API unreachable" (with last-seen timestamp on hover)
 *
 * We use a bare axios call (NOT the shared `api` client) on purpose:
 *   - No auth-refresh interceptor noise on health pings.
 *   - A 401/403 still counts as "online" because it proves the server
 *     responded.
 */
const POLL_MS = 60_000;
const PING_TIMEOUT_MS = 6_000;

export default function AdminHealthBadge() {
  const [status, setStatus] = useState("checking");
  const [lastOkAt, setLastOkAt] = useState(null);

  useEffect(() => {
    let alive = true;

    const ping = async () => {
      try {
        const r = await axios.get(`${API}/`, { timeout: PING_TIMEOUT_MS });
        if (!alive) return;
        if (r.status >= 200 && r.status < 500) {
          setStatus("ok");
          setLastOkAt(new Date());
          return;
        }
        setStatus("down");
      } catch (_) {
        if (!alive) return;
        setStatus("down");
      }
    };

    ping();
    const id = setInterval(ping, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const styles =
    status === "ok"
      ? {
          dot: "bg-[#16A34A] shadow-[0_0_0_3px_rgba(22,163,74,0.18)]",
          label: "API online",
          text: "text-[#15803D]",
          ring: "border-[#16A34A]/30 bg-[#16A34A]/5",
        }
      : status === "down"
      ? {
          dot: "bg-[#DC2626] animate-pulse",
          label: "API unreachable",
          text: "text-[#B91C1C]",
          ring: "border-[#DC2626]/30 bg-[#DC2626]/5",
        }
      : {
          dot: "bg-black/30",
          label: "Checking…",
          text: "text-[var(--brand-body)]",
          ring: "border-black/10 bg-white",
        };

  const title =
    status === "ok" && lastOkAt
      ? `Last successful ping: ${lastOkAt.toLocaleTimeString("en-GB")}`
      : status === "down"
      ? "The /api endpoint did not respond. Public site may be falling back to defaults."
      : "Running health check…";

  return (
    <span
      title={title}
      data-testid="admin-health-badge"
      data-status={status}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 label-kicker ${styles.ring} ${styles.text}`}
    >
      <span className={`h-2 w-2 rounded-full ${styles.dot}`} />
      {styles.label}
    </span>
  );
}
